import { useEffect, useMemo, useState } from "react";
import type { CalendarEvent, EventsResponse } from "../../shared/types.js";
import { CALENDARS_BY_ID } from "../../shared/calendars.js";
import { dayKey, todayHawaiiKey } from "../lib/eventTime.js";
import { navigate } from "../router.js";
import { EventModal } from "../components/EventModal.js";

type MonthKey = { year: number; month: number /* 0-indexed */ };

function todayMonth(): MonthKey {
  const today = todayHawaiiKey();
  const [y, m] = today.split("-");
  return { year: Number(y), month: Number(m) - 1 };
}

function shiftMonth(mk: MonthKey, delta: number): MonthKey {
  const total = mk.year * 12 + mk.month + delta;
  return { year: Math.floor(total / 12), month: total % 12 };
}

function monthLabel(mk: MonthKey): string {
  return new Date(mk.year, mk.month, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

/** Build the 6×7 grid for the visible month (always 42 cells, including
 *  trailing days from prev/next months for a clean rectangle). */
function gridDays(mk: MonthKey): { key: string; inMonth: boolean; day: number }[] {
  const first = new Date(mk.year, mk.month, 1);
  const startDayOfWeek = first.getDay(); // 0 = Sunday
  const cells: { key: string; inMonth: boolean; day: number }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(mk.year, mk.month, 1 - startDayOfWeek + i);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    cells.push({
      key: `${yyyy}-${mm}-${dd}`,
      inMonth: d.getMonth() === mk.month,
      day: d.getDate(),
    });
  }
  return cells;
}

export function CalendarGrid() {
  const [data, setData] = useState<EventsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [month, setMonth] = useState<MonthKey>(todayMonth());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Pull a wide window — 60 days back, 180 days forward — to populate the grid
    // for nearby months without hitting the API on every navigation click.
    const from = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();
    const to = new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString();
    fetch(`/api/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
      .then((r) => r.json())
      .then((d: EventsResponse) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
  }, []);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    if (!data) return map;
    for (const ev of data.events) {
      const k = dayKey(ev);
      let bucket = map.get(k);
      if (!bucket) {
        bucket = [];
        map.set(k, bucket);
      }
      bucket.push(ev);
    }
    return map;
  }, [data]);

  const cells = useMemo(() => gridDays(month), [month]);
  const today = todayHawaiiKey();

  return (
    <section className="page-calendar">
      <header className="grid-head">
        <h1>Calendar</h1>
        <p className="muted">
          Browse by month. Tap any event for full details.{" "}
          <a
            href="/calendar"
            onClick={(e) => {
              e.preventDefault();
              navigate("/calendar");
            }}
          >
            Switch to list view
          </a>
          .
        </p>
        <div className="grid-nav">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setMonth(shiftMonth(month, -1))}
            aria-label="Previous month"
          >
            ← Prev
          </button>
          <h2 className="grid-month-label">{monthLabel(month)}</h2>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setMonth(shiftMonth(month, 1))}
            aria-label="Next month"
          >
            Next →
          </button>
          <button
            type="button"
            className="btn btn-secondary grid-today-btn"
            onClick={() => setMonth(todayMonth())}
            disabled={month.year === todayMonth().year && month.month === todayMonth().month}
          >
            Today
          </button>
        </div>
      </header>

      {error && <p className="error-banner">Couldn't load events: {error}</p>}

      <div className="grid-table" role="grid" aria-label={`Events for ${monthLabel(month)}`}>
        <div className="grid-weekdays" role="row">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="grid-weekday" role="columnheader">
              {d}
            </div>
          ))}
        </div>
        <div className="grid-cells">
          {cells.map((cell) => {
            const dayEvents = eventsByDay.get(cell.key) ?? [];
            return (
              <div
                key={cell.key}
                className={`grid-cell${cell.inMonth ? "" : " is-other-month"}${
                  cell.key === today ? " is-today" : ""
                }`}
                role="gridcell"
              >
                <div className="grid-cell-day">{cell.day}</div>
                <ul className="grid-cell-events">
                  {dayEvents.map((ev) => {
                    const source = CALENDARS_BY_ID.get(ev.sourceId);
                    const color = source?.color ?? "#666";
                    return (
                      <li key={ev.id}>
                        <button
                          type="button"
                          className="grid-event"
                          style={{ borderLeftColor: color }}
                          onClick={() => setSelectedEvent(ev)}
                          title={ev.title}
                        >
                          {ev.title || "(no title)"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {selectedEvent && <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />}
    </section>
  );
}
